import {
	faExternalLink,
	faGear,
	faGears,
	faHouse,
	faSquarePollHorizontal,
	faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { Burger, Drawer, NavLink } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { ReactNode } from 'react';
import { RestrictedContent } from '../../../app/(client)/(authenticated)/content/restricted/RestrictedContent';
import { ConditionalNavLink } from '../../../app/(client)/(authenticated)/jobs/(jobId)/jobLink/ConditionalNavLink';
import { getReportProblemLink } from '../../../app/(client)/(authenticated)/reportProblem/_components/getReportProblemLink';
import { AppAction } from '../../../contexts/TokenContext/Roles';
import { useTriggerOnPathChange } from '../../../hooks/usePathChanged';
import { Icon } from '../../Spinner/Icon';
import styles from './MenuButton.module.scss';
import { MenuSummary } from './summary/MenuSummary';

const GraphIcon = <Icon icon={faSquarePollHorizontal} aria-label="Graphs" />;
const ExternalLinkIcon = <Icon icon={faExternalLink} aria-label="External Link" />;
const GearIcon = <Icon icon={faGear} aria-label="Settings" />;
const GearsIcon = <Icon icon={faGears} aria-label="Settings" />;
const WrenchIcon = <Icon icon={faWrench} aria-label="Wrench" />;
const HouseIcon = <Icon icon={faHouse} aria-label="Building" />;

function MenuContent(): ReactNode {
	const nonPremiseReportLink = getReportProblemLink();

	return (
		<>
			<MenuSummary />

			<RestrictedContent action={AppAction.Summary}>
				<ConditionalNavLink leftSection={GraphIcon} href="/summary/" label="Summary" />
			</RestrictedContent>

			<RestrictedContent action={AppAction.SearchOrders}>
				<ConditionalNavLink leftSection={HouseIcon} href="/premises/" label="Premise Search" />
				<ConditionalNavLink leftSection={HouseIcon} href={nonPremiseReportLink} label="Non-Premise Report" />
				<ConditionalNavLink leftSection={WrenchIcon} href="/jobs/" label="Jobs" />
			</RestrictedContent>

			<RestrictedContent action={AppAction.ConfigMenu}>
				<br />
				<h3>Config</h3>
				<RestrictedContent action={AppAction.EditCounts}>
					<ConditionalNavLink leftSection={GearIcon} href="/config/counts/" label="Counts" />
				</RestrictedContent>
				<RestrictedContent action={AppAction.SwitchMode}>
					<ConditionalNavLink leftSection={GearIcon} href="/config/modes/" label="Storm Mode" />
				</RestrictedContent>

				<RestrictedContent action={AppAction.EditModes}>
					<NavLink leftSection={GearsIcon} href="#required-for-focus" label="Messages" childrenOffset={28}>
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/messages/causeCodes/"
							label="Cause Code"
						/>
						<ConditionalNavLink leftSection={GearIcon} href="/config/messages/errors/" label="Errors" />
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/messages/estimates/"
							label="Estimates"
						/>
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/messages/estimateDates/"
							label="Estimate Dates"
						/>
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/messages/notifications/"
							label="Notifications"
						/>
						<ConditionalNavLink leftSection={GearIcon} href="/config/messages/reports/" label="Reports" />
						<ConditionalNavLink leftSection={GearIcon} href="/config/messages/status/" label="Status" />
						<ConditionalNavLink leftSection={GearIcon} href="/config/messages/empathy/" label="Empathy" />
						<ConditionalNavLink leftSection={GearIcon} href="/config/messages/variants/" label="Variants" />
					</NavLink>
				</RestrictedContent>
				<RestrictedContent action={AppAction.EditSettings}>
					<NavLink leftSection={GearsIcon} href="#required-for-focus" label="Settings" childrenOffset={28}>
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/settings/dataChecks/"
							label="Data Checks"
						/>
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/settings/estimates/"
							label="Estimates"
						/>
						<ConditionalNavLink leftSection={GearIcon} href="/config/settings/logging/" label="Logging" />
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/settings/notifications/"
							label="Notifications"
						/>
						<ConditionalNavLink
							leftSection={GearIcon}
							href="/config/settings/recentlyClosed/"
							label="Opening / Closing"
						/>
					</NavLink>
				</RestrictedContent>
			</RestrictedContent>

			<br />
			<h3>Customer Links</h3>
			<NavLink leftSection={ExternalLinkIcon} href="https://outage.dteenergy.com/" label="Outage Center" />
			<NavLink leftSection={ExternalLinkIcon} href="https://outage.dteenergy.com/map" label="Outage Map" />
			<NavLink
				leftSection={ExternalLinkIcon}
				href="https://www.dteenergy.com/us/en/residential/emergency-and-safety/safety/outage-faqs.html"
				label="Outage FAQ"
			/>

			<RestrictedContent action={AppAction.EditModes}>
				<br />
				<h3>Internal Links</h3>
				<NavLink
					leftSection={ExternalLinkIcon}
					href="https://app.sos.sites.dteenergy.com/"
					label="System Outage Screen (SOS)"
				/>
				<NavLink
					leftSection={ExternalLinkIcon}
					href="https://kubra.io/stormcenter/views/ca940bc4-81c4-4d22-a2b0-dd338f5a9a32"
					label="Outage Map"
				/>
			</RestrictedContent>
		</>
	);
}

export function MenuButton(): ReactNode {
	const [opened, { toggle, close }] = useDisclosure();

	// Close this when changing paths
	useTriggerOnPathChange(close);

	return (
		<nav className={styles['nav']}>
			<Drawer className={styles['drawer']} opened={opened} onClose={close} title="Menu">
				<MenuContent />
			</Drawer>
			<Burger lineSize={3} color="white" onClick={toggle} title="Toggle Menu" />
		</nav>
	);
}
